// react
import { useCallback, useEffect, useRef, useState } from "react";
// mui components
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
//google map react
import GoogleMap from "google-map-react";
//custom component, utils
import { is } from "src/util/is";
//images
// import ChargerMilesMarker from "../../image/chargemiles.svg";
import { useAuth } from "src/hooks/useAuth.hook";

const MAP_STYLE = [
  {
    elementType: "geometry",
    stylers: [
      {
        color: "#f5f5f5",
      },
    ],
  },
  {
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#616161",
      },
    ],
  },
  {
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#f5f5f5",
      },
    ],
  },
  {
    featureType: "administrative",
    elementType: "geometry",
    stylers: [
      {
        color: "#e0e0e0",
      },
    ],
  },
  {
    featureType: "administrative.country",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#9e9e9e",
      },
    ],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [
      {
        color: "#e8f5e8",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#4caf50",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#ffffff",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#ffffff",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#424242",
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [
      {
        color: "#f5f5f5",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [
      {
        color: "#e0e0e0",
      },
    ],
  },
  {
    featureType: "road.highway.controlled_access",
    elementType: "geometry",
    stylers: [
      {
        color: "#d0d0d0",
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#616161",
      },
    ],
  },
  {
    featureType: "transit",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#757575",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [
      {
        color: "#e3f2fd",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#1976d2",
      },
    ],
  },
];

const domainIcon = {
  amiktechnologies: "https://avvmedia.blob.core.windows.net/avv-media/networkFilters/amik.png",
  moveonev:"https://avvmedia.blob.core.windows.net/avv-media/networkFilters/moveonev.png",
  techvoltnetwork:"https://avvmedia.blob.core.windows.net/avv-media/networkFilters/techvoltNetwork.png",
  acsenergy: "https://avvmedia.blob.core.windows.net/avv-media/networkFilters/acsICON.png",
  avvtech:"https://avvmedia.blob.core.windows.net/avv-media/networkFilters/amik.png",
  zerofootprint: "https://avvmedia.blob.core.windows.net/avv-media/networkFilters/zeroFootPrint.jpg"
};

function Map({ onPlaces, lat, lng, address, searchBar = true, data = null, showMessage = true, disabled = false }) {
  const { user } = useAuth();
  const userDomain = user?.domain;
  const markerIconUrl = domainIcon[userDomain] || "https://avvmedia.blob.core.windows.net/avv-media/networkFilters/amik.png";
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  let markers = useRef([]);
  const onSearchBar = useCallback((map) => {
    let markers = [];
    const {
      places: { SearchBox },
      LatLngBounds,
      Size,
      Point,
      Marker,
      LatLng,
      Geocoder,
    } = window.google.maps;
    const input = document.getElementById("places");
    
    // Add null check for input element
    if (!input) {
      console.warn("Places input element not found");
      return;
    }
    
    const searchBox = new SearchBox(input);
    const geocoder = new Geocoder();
    
    map.addListener("bounds_changed", () => {
      searchBox.setBounds(map.getBounds());
    });

    map.addListener("center_changed", () => {
      map.setZoom(13);
    });

    // Add click listener for dropping pins (only when not disabled)
    if (!disabled) {
    map.addListener("click", (event) => {
      try {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        
        // Clear existing markers
        markers.forEach((marker) => marker.setMap(null));
        markers = [];
        
        // Create new marker at clicked location
        const marker = new Marker({
          position: { lat, lng },
          map,
          icon: {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="12" fill="#1976d2" stroke="#ffffff" stroke-width="3"/>
                <circle cx="16" cy="16" r="6" fill="#ffffff"/>
              </svg>
            `),
            size: new Size(32, 32),
            origin: new Point(0, 0),
            anchor: new Point(16, 16),
            scaledSize: new Size(32, 32),
          },
          title: "Selected Location",
        });
        
        markers.push(marker);
        
        // Reverse geocode to get address
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status === "OK" && results[0]) {
            const place = {
              address_components: results[0].address_components,
              geometry: { location: { lat: () => lat, lng: () => lng } },
              formatted_address: results[0].formatted_address,
            };
            onPlaces([place]);
            
            // Update search input with the address
            input.value = results[0].formatted_address;
          }
        });
      } catch (error) {
        console.error("Error in map click listener:", error);
      }
    });
    }

    searchBox.addListener("places_changed", () => {
      try {
        const places = searchBox.getPlaces();
        
        if (!places || !places.length) {
          return;
        }
        
        const place = places[0];
        if (!place || !place.geometry || !place.geometry.location) {
          return;
        }
        
        const {
          geometry: { location },
        } = place;
        const center = new LatLng(location.lat(), location.lng());
        const bounds = new LatLngBounds();
        // clear old markers
        markers.forEach((marker) => marker.setMap(null));
        markers = [];
        places.forEach((place) => {
          if (!place.geometry || !place.geometry.location) {
            return;
          }
          const icon = {
            url: place.icon,
            size: new Size(71, 71),
            origin: new Point(0, 0),
            anchor: new Point(17, 34),
            scaledSize: new Size(28, 28),
          };
          markers.push(
            new Marker({
              map,
              icon,
              title: place.name,
              position: place.geometry.location,
            })
          );
          if (place.geometry.viewport) {
            // Only geocodes have viewport.
            bounds.union(place.geometry.viewport);
          } else {
            bounds.extend(place.geometry.location);
          }
        });
        map.fitBounds(bounds);
        map.setCenter(center);
        onPlaces(places);
      } catch (error) {
        console.error("Error in places_changed listener:", error);
      }
    });
    setMap(map);
  }, []);

  const onMarker = useCallback(
    (map) => {
      try {
        if (data && map && window.google) {
          const { LatLng, Marker, Size, Point, InfoWindow } = window.google.maps;
          if (markers.current.length) {
            markers.current.forEach((marker) => {
              if (marker && marker.setMap) {
                return marker.setMap(null);
              }
            });
            markers.current = [];
          }
          
          data.forEach((d, index) => {
            const lat = Number(d.locationLatitude);
            const lng = Number(d.locationLongitude);
            
            // Skip if coordinates are invalid
            if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
              return;
            }
            
            const latLng = new LatLng(lat, lng);
            const infoWindow = new InfoWindow({
              content: `
                <div style="
                  padding: 12px 16px;
                  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                  border-radius: 8px;
                  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
                  border: 1px solid rgba(0,0,0,0.1);
                  font-family: 'Roboto', sans-serif;
                  font-size: 14px;
                  color: #424242;
                  max-width: 200px;
                  line-height: 1.4;
                ">
                  <div style="
                    font-weight: 600;
                    color: #1976d2;
                    margin-bottom: 4px;
                  ">📍 ${d.street || 'Charging Station'}</div>
                  <div style="
                    font-size: 12px;
                    color: #757575;
                  ">Click to view details</div>
                </div>
              `,
              ariaLabel: "address",
            });
            const marker = new Marker({
              position: latLng,
              icon: {
                url: markerIconUrl,
                size: new Size(71, 71),
                origin: new Point(0, 0),
                anchor: new Point(17, 34),
                scaledSize: new Size(28, 28),
              },
              map,
            });
            markers.current.push(marker);
            marker.addListener("mouseover", () => {
              infoWindow.open({
                anchor: marker,
                map,
              });
            });

            marker.addListener("mouseout", () => {
              infoWindow.close();
            });
          });
          
          
          map.addListener("center_changed", () => {
            map.setZoom(4);
          });
          map.setOptions({ styles: MAP_STYLE });
          map.setCenter(new LatLng(20.5937, 78.9629));
        }
      } catch (error) {
        console.error("Error in onMarker function:", error);
      }
    },
    [data, markerIconUrl]
  );

  const handleMapApiLoaded = useCallback(({ map }) => {
    setMap(map);
    if (window.google) {
      onSearchBar(map);
      setMapReady(true);
    }
  }, [onSearchBar, data]);

  useEffect(() => {
    if (lat && lng && map && !data) {
      const { LatLng, Marker } = window.google.maps;
      const input = document.getElementById("places");
      
      const position = new LatLng(lat, lng);
      if (input && !is.empty(address)) {
        input.value = address;
      }
      map.setCenter(position);
      if (markers?.current?.length) {  
        markers.current.forEach((marker) => {
          return marker.setMap(null);
        });
        markers.current = [];
      }
      markers.current.push(
        new Marker({
          position,
          map,
        })
      );
    }

    
    if (data && map && mapReady) {
      onMarker(map);
    } else {
    }
  }, [lat, lng, address, map, data, onMarker, mapReady]);

  // Separate effect for when data becomes available
  useEffect(() => {
    
    if (data && data.length > 0 && map && mapReady) {
      onMarker(map);
    } else {
    }
  }, [data, map, mapReady, onMarker]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clean up markers when component unmounts
      if (markers?.current?.length) {
        markers.current.forEach((marker) => {
          if (marker && marker.setMap) {
            marker.setMap(null);
          }
        });
        markers.current = [];
      }
    };
  }, []);

  return (
    <Stack direction="column" spacing={2}>
      {searchBar && (
        <FormControl fullWidth size="small">
          <TextField
            label="Search Place"
            InputLabelProps={{ shrink: true }}
            size="small"
            id="places"
            placeholder="Search for an address or click on the map to drop a pin"
            disabled={disabled}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 4px 16px rgba(25, 118, 210, 0.2)',
                }
              }
            }}
          />
        </FormControl>
      )}
      {showMessage && !disabled && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.5,
          backgroundColor: 'rgba(25, 118, 210, 0.05)',
          borderRadius: 2,
          border: '1px solid rgba(25, 118, 210, 0.1)',
          fontSize: '0.875rem',
          color: '#1976d2',
          fontWeight: 500
        }}>
          <Box sx={{
            width: 20,
            height: 20,
            borderRadius: '50%',
            backgroundColor: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            📍
          </Box>
          Click anywhere on the map to drop a pin and select that location
        </Box>
      )}
      <div 
        className="map-container"
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          border: '1px solid rgba(0,0,0,0.08)',
          height: '400px',
          position: 'relative',
          background: 'linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 100%)',
          cursor: 'crosshair',
        }}
      >
        <GoogleMap
          bootstrapURLKeys={{
           // key: "AIzaSyDUgxJLnLjZm3fV_24nDDB-2BKMNqmra9I",
            key: "AIzaSyB5VfSNqyhjYLoiXpI94yp5H2TPBgOVitY",
            libraries: ["places"],
          }}
          defaultCenter={{
            lat: 20.5937,
            lng: 78.9629,
          }}
          defaultZoom={4}
          onGoogleApiLoaded={handleMapApiLoaded}
          yesIWantToUseGoogleMapApiInternals={true}
          options={{
            styles: MAP_STYLE,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: true,
            rotateControl: true,
            fullscreenControl: true,
            gestureHandling: 'greedy',
            clickableIcons: true,
            keyboardShortcuts: true,
          }}
        />
        {/* Map overlay for enhanced visual appeal */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(25,118,210,0.05) 100%)',
            pointerEvents: 'none',
            borderRadius: '12px',
          }}
        />
      </div>
    </Stack>
  );
}

Map.defaultProps = {
  onPlaces: () => {},
  lat: 0,
  lng: 0,
  address: "",
};

export default Map;
